import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithReauth from '../../app/baseQueryWithOnlineCourse'
import {
    GroupSectionAnalysisState,
    ListResponse,
    SectionAnalysisState,
    SectionDetailsState,
    SectionState,
    StudentSectionAnalysisState,
} from '../types'

interface getStudentsSectionAnalysisByGroupParams {
    groupId: number
    size: number
    page: number
    monthNumber?: number
    weekNumber?: number
}

interface getStudentSectionAnalysisParams {
    id: number
    typeEnum: string
    page: number
    rowsPerPage: number
    filter: string
    groupId: number
}

export const sectionApiSlice = createApi({
    reducerPath: 'sectionApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Section', 'Student', 'Group', 'StudentSectionAnalysis'],
    endpoints: (build) => ({
        getSectionsByCourseTopic: build.query<any, number>({
            query: (id) => ({
                url: `course-topic/video-and-test/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'Section' as const,
                              id,
                          })),
                          { type: 'Section', id: 'LIST' },
                      ]
                    : [{ type: 'Section', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        updateSectionOrders: build.mutation<void, any>({
            query: (section) => ({
                url: 'course-topic/video-and-test',
                method: 'PUT',
                body: section,
            }),
        }),

        getStudentSectionsByTopicEnroll: build.query<SectionState[], number>({
            query: (userTopicEnrollId) => ({
                url: `user-topic/video-and-test/${userTopicEnrollId}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'Section' as const,
                              id,
                          })),
                          'Section',
                      ]
                    : ['Section'],
            transformResponse: (response: any) => response.data,
        }),

        getSectionDetails: build.query<SectionDetailsState, SectionState>({
            query: (section) => ({
                url: `user-topic/teacher/video-and-test/data/${section.id}?typeEnum=${section.type}`,
            }),
            transformResponse: (response: any) => response.data,
        }),

        getSectionDetailsByTopicEnroll: build.query<
            SectionDetailsState,
            SectionState
        >({
            query: (section) => ({
                url: `user-topic/video-and-test/data/${section.id}?typeEnum=${section.type}&userTopicEnrollId=${section.userTopicEnrollId}`,
            }),
            transformResponse: (response: any) => response.data,
        }),

        getStudentsSectionAnalysisByGroup: build.query<
            ListResponse<StudentSectionAnalysisState>,
            getStudentsSectionAnalysisByGroupParams
        >({
            query: (params) => ({
                url: `student-group/students/analysis/${params.groupId}`,
                params: {
                    page: params.page,
                    size: params.size,
                    monthNumber: params.monthNumber,
                    weekNumber: params.weekNumber,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.data.map(({ userId }) => ({
                              type: 'Student' as const,
                              userId,
                          })),
                          { type: 'Student', id: 'LIST' },
                      ]
                    : [{ type: 'Student', id: 'LIST' }],
            transformResponse: (response: {
                data: ListResponse<StudentSectionAnalysisState>
            }) => ({
                data: response.data.data,
                pageNumber: response.data.pageNumber,
                totalCount: response.data.totalCount,
                totalPages: response.data.totalPages,
            }),
        }),

        getStudentSectionAnalysis: build.query<
            ListResponse<SectionAnalysisState>,
            getStudentSectionAnalysisParams
        >({
            query: ({ id, typeEnum, page, rowsPerPage, filter, groupId }) => ({
                url: `user-topic/video-and-test/students/analysis/${id}`,
                params: {
                    filter,
                    groupId,
                    typeEnum,
                    page,
                    size: rowsPerPage,
                },
            }),
            providesTags: ['Student'],
            transformResponse: (response: {
                data: ListResponse<SectionAnalysisState>
            }) => ({
                data: response.data.data,
                pageNumber: response.data.pageNumber,
                totalCount: response.data.totalCount,
                totalPages: response.data.totalPages,
            }),
        }),

        getGroupsSectionAnalysis: build.query<
            GroupSectionAnalysisState[],
            { curatorId: number; monthNumber?: number; weekNumber?: number }
        >({
            query: ({ curatorId, monthNumber, weekNumber }) => ({
                url: `student-group/curator-group/analysis/${curatorId}`,
                params: { monthNumber, weekNumber },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ groupId }) => ({
                              type: 'Group' as const,
                              groupId,
                          })),
                          { type: 'Group', id: 'LIST' },
                      ]
                    : [{ type: 'Group', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getTopicSections: build.query<SectionState[], number>({
            query: (courseTopicId) => ({
                url: `user-topic/teacher/video-and-test/${courseTopicId}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'Section' as const,
                              id,
                          })),
                          { type: 'Section', id: 'LIST' },
                      ]
                    : [{ type: 'Section', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),
    }),
})
